import { CheckOutlined, TeamOutlined } from '@ant-design/icons';
import { Button, ButtonProps } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import { SalesForceIcon } from '@mm/icons';
import { useSalesForceCRUDActions } from '@mm/state/actions/SalesForceCRUDActions';
import { selectCrmsLeadsSent } from '@mm/state/selectors/connectionsSelectors';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { OAuthPopupActionButton } from '@mm/stateful-components/OAuth/OAuthPopupActionButton';
import React, { ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ButtonColor } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { BaseIntegration } from '../../../BaseIntegration/BaseIntegration';
import { BaseIntegrationHeader } from '../../../BaseIntegration/BaseIntegrationHeader';
import { Help } from '../KlaviyoIntegrations/KlaviyoHelpLink';
import { SalesForceSettings } from './SalesForceSettings';
import { useConnectSalesForce } from './use-connect-salesforce';

export const SalesForceIntegrations = () => {
  const hasNativeIntegrationsFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).nativeIntegrations;
  const oAuthButtonName = 'Add a SalesForce Account';
  const { invalidateStore } = useSalesForceCRUDActions();
  const {
    accounts,
    retrieveSalesForceAccounts,
    fetchOAuthUrl,
    onTokenConnected,
    salesForceConnections,
    getSaleForceConnections,
  } = useConnectSalesForce();
  const [accountsFetched, setAccountsFetched] = useState(false);
  const connection = salesForceConnections[0];

  const connectedToken = accounts.find(
    token => token.id === connection?.salesforceAccessToken?.id,
  );
  const showSettings = !!connection && !!connectedToken;
  const connectDisabled = connectedToken?.readOnly;

  const sectionProps = {
    landmineName: landmineNames.SALESFORCE_UPGRADE,
    featureFlag: 'nativeIntegrations',
  };
  const otherProps = {
    ...sectionProps,
    popupWidth: 600,
    popupHeight: 600,
    onRefreshNeeded: onTokenConnected,
    connectParams: [connectedToken?.id],
  };

  // loads webhooks once oauth tokens/api keys are loaded
  useEffect(() => {
    if (accountsFetched) {
      getSaleForceConnections();
    }
  }, [accountsFetched]);

  useEffect(() => {
    (async () => {
      await retrieveSalesForceAccounts();
      setAccountsFetched(true);
    })();
    return () => {
      invalidateStore();
    };
  }, []);

  const oAuthButton = (
    buttonName?: string | ReactNode,
    buttonProps?: Pick<ButtonProps, 'size' | 'color' | 'css' | 'disabled'>,
  ) => {
    const defaultButtonProps = {
      css: {
        container: 'integrations__header-add-button-secondary',
      },
    };

    return (
      <OAuthPopupActionButton
        fetchOauthPopupUrl={fetchOAuthUrl}
        oauthSuccessMessageType="salesForceTokenReceived"
        buttonProps={buttonProps || defaultButtonProps}
        popupName="salesForceConnect"
        spinnerProps={{ width: '30px' }}
        {...otherProps}
      >
        {buttonName || 'Add an Account'}
      </OAuthPopupActionButton>
    );
  };

  const crmsLeadsSent = useSelector(selectCrmsLeadsSent);
  const leadsSent = crmsLeadsSent?.salesforceContactsSent;

  const { companyName = 'Customers.ai' } = useWhitelabelConfig();

  const header = () => (
    <div className="integrations__header-actions">
      <Help
        link="https://customers.ai/help/l/en/article/oqqh8uzfjl-sales-force"
        text="Help Center"
      />
      {!!leadsSent && (
        <div className="integrations__header-status">
          <TeamOutlined className="integrations__header-status-icon" />
          <div className="integrations__header-status-count">{leadsSent}</div>
          Leads sent
        </div>
      )}
      {connectDisabled ? (
        <Button
          css={{ container: 'integrations__header-add-button-secondary' }}
          color={ButtonColor.secondaryBlue}
          disabled={connectDisabled}
        >
          <CheckOutlined /> Connected Directly
        </Button>
      ) : (
        oAuthButton()
      )}
    </div>
  );

  return (
    <BaseIntegration
      titleComponent={
        <BaseIntegrationHeader
          title="SalesForce"
          upgradeButtonName={
            !connectDisabled ? 'Add an Account' : 'Add a Connection'
          }
          subtitle={`Automatically sync your ${companyName} contacts to SalesForce`}
          icon={<SalesForceIcon />}
          headerRightComponent={header()}
          css={{ icon: 'integrations__salesforce-header-icon ' }}
          {...sectionProps}
        />
      }
      integrationType="salesforce"
      emptyStateConfig={{
        button: hasNativeIntegrationsFeature
          ? oAuthButton(oAuthButtonName, {
              css: { container: 'integrations__empty-state-button' },
            })
          : oAuthButtonName,
        icon: <SalesForceIcon size={60} />,
        ...sectionProps,
      }}
      showEmptyState={!showSettings}
    >
      {showSettings && (
        <SalesForceSettings
          account={connectedToken}
          oAuthButton={oAuthButton}
        />
      )}
    </BaseIntegration>
  );
};
